import { clientQueryKeys } from "@/features/clients/api/query-keys";

type JobBoardInvoiceListParams = {
  tenantId?: string | null;
  descending?: boolean;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  search?: string;
};

type JobBoardInvoiceDetailParams = {
  tenantId?: string | null;
  descending?: boolean;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  search?: string;
};

type ChannelInvoiceListParams = {
  tenantId?: string | null;
  descending?: boolean;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  isInvoiced?: boolean;
};

type ChannelInvoiceDetailParams = {
  tenantId?: string | null;
  descending?: boolean;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  search?: string;
};

export const invoiceQueryKeys = {
  all: ["invoices"] as const,
  byTenant: (tenantId: string | null) =>
    [...invoiceQueryKeys.all, tenantId] as const,
  jobBoards: {
    all: () => [...invoiceQueryKeys.all, "jobBoards"] as const,
    lists: () => [...invoiceQueryKeys.jobBoards.all(), "list"] as const,
    list: (params: JobBoardInvoiceListParams) =>
      [
        ...invoiceQueryKeys.jobBoards.lists(),
        params.tenantId,
        params.descending,
        params.orderBy,
        params.page,
        params.pageSize,
        params.search,
      ] as const,
    details: () => [...invoiceQueryKeys.jobBoards.all(), "detail"] as const,
    detail: (
      jobBoardId: string,
      year: string,
      month: string,
      params: JobBoardInvoiceDetailParams
    ) =>
      [
        ...invoiceQueryKeys.jobBoards.details(),
        jobBoardId,
        year,
        month,
        params.tenantId,
        params.descending,
        params.orderBy,
        params.page,
        params.pageSize,
        params.search,
      ] as const,
  },
  channels: {
    all: () => [...invoiceQueryKeys.all, "channels"] as const,
    lists: () => [...invoiceQueryKeys.channels.all(), "list"] as const,
    list: (params: ChannelInvoiceListParams) =>
      [
        ...invoiceQueryKeys.channels.lists(),
        params.tenantId,
        params.descending,
        params.isInvoiced,
        params.orderBy,
        params.page,
        params.pageSize,
        params.search,
      ] as const,
    details: () => [...invoiceQueryKeys.channels.all(), "detail"] as const,
    detail: (
      channelId: string,
      year: string,
      month: string,
      params: ChannelInvoiceDetailParams
    ) =>
      [
        ...invoiceQueryKeys.channels.details(),
        channelId,
        year,
        month,
        params.tenantId,
        params.descending,
        params.orderBy,
        params.page,
        params.pageSize,
        params.search,
      ] as const,
  },
  // Query keys for cache invalidation when invoices are updated
  cache: {
    // Channel month queries
    channelMonths: {
      all: () => ["channelMonths"] as const,
      byChannel: (channelId: string) =>
        [...invoiceQueryKeys.cache.channelMonths.all(), channelId] as const,
      byClientAndChannel: (clientId: string, channelId: string) =>
        [
          ...invoiceQueryKeys.cache.channelMonths.all(),
          clientId,
          channelId,
        ] as const,
      byChannelYearMonth: (channelId: string, year: string, month: string) =>
        [
          ...invoiceQueryKeys.cache.channelMonths.all(),
          channelId,
          year,
          month,
        ] as const,
    },

    // Campaign job board month queries
    campaignJobBoardMonths: {
      all: () => ["campaignJobBoardMonths"] as const,
      byClient: (clientId: string) =>
        [
          ...invoiceQueryKeys.cache.campaignJobBoardMonths.all(),
          clientId,
        ] as const,
      byClientYearMonth: (clientId: string, month: string, year: string) =>
        [
          ...invoiceQueryKeys.cache.campaignJobBoardMonths.all(),
          clientId,
          undefined,
          month,
          undefined,
          undefined,
          year,
        ] as const,
      byJobBoard: () => ["campaignJobBoardMonthsByJobBoard"] as const,
    },

    // Job board campaign queries (legacy)
    jobBoardCampaigns: {
      all: () => ["jobBoardCampaigns"] as const,
      byJobBoardYearMonth: (jobBoardId: string, year: string, month: string) =>
        [
          ...invoiceQueryKeys.cache.jobBoardCampaigns.all(),
          jobBoardId,
          year,
          month,
        ] as const,
    },

    // Integration module campaign queries (tenant pages use tenantIntegrationModuleId)
    integrationModuleCampaigns: {
      all: () => ["integrationModuleCampaigns"] as const,
      byIntegrationModuleYearMonth: (
        tenantIntegrationModuleId: string,
        year: string,
        month: string
      ) =>
        [
          ...invoiceQueryKeys.cache.integrationModuleCampaigns.all(),
          tenantIntegrationModuleId,
          year,
          month,
        ] as const,
    },

    // Integration module invoice queries (invoiced tab)
    integrationModuleInvoices: {
      all: () => ["integrationModuleInvoices"] as const,
      detail: (
        tenantIntegrationModuleId: string,
        year: string,
        month: string
      ) =>
        [
          ...invoiceQueryKeys.cache.integrationModuleInvoices.all(),
          tenantIntegrationModuleId,
          year,
          month,
        ] as const,
    },

    // Client channel queries
    clientChannels: {
      all: () => ["clientChannels"] as const,
      byClient: (clientId?: string) =>
        [...invoiceQueryKeys.cache.clientChannels.all(), clientId] as const,
      byClientYearMonth: (clientId: string, month: string, year: string) =>
        [
          ...invoiceQueryKeys.cache.clientChannels.all(),
          clientId,
          month,
          year,
        ] as const,
    },

    // Client budget month queries
    clientBudgetMonth: {
      all: () => ["clientBudgetMonth"] as const,
    },

    // Shared query keys (used across features but needed for invoice invalidations)
    clients: {
      all: () => clientQueryKeys.all,
      byId: (clientId: string) => clientQueryKeys.detail(clientId),
    },

    transactions: {
      all: () => ["transactions"] as const,
      byClient: (clientId: string) =>
        [...invoiceQueryKeys.cache.transactions.all(), clientId] as const,
    },

    channels: {
      all: () => ["channels"] as const,
    },

    tenantJobBoards: {
      all: () => ["tenantJobBoards"] as const,
      byTenant: (tenantId: string | null) =>
        [...invoiceQueryKeys.cache.tenantJobBoards.all(), tenantId] as const,
    },
  },
};
